import { CustomFields, CompetitionCustomFieldOptions } from './../../../models/inModels/Competition';
import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CompetitionService } from '../../../services/competition/competition.service';
import { CompetitionEntry, EditCompetition } from '../../../models/inModels/Competition';
import { ActivatedRoute, Router } from '@angular/router';
import { Message, SelectItem } from 'primeng/primeng';
import { StorageService } from '../../../services/system/storage.service';
import { LazyLoadEvent } from '../../../models/DataTables/lazyLoadEvent';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-edit-competition',
  templateUrl: './edit-competition.component.html',
  styleUrls: ['./edit-competition.component.css']
})
export class EditCompetitionComponent implements OnInit {
  id;
  loading = false;
  model: EditCompetition;
  error: Message[];
  entries: CompetitionEntry[] = [];
  CustomFields: CustomFields[];
  CompetitionCustomFieldOptions: CompetitionCustomFieldOptions[];
  downloading = false;
  entriesRows = 10;
  entriesLoading = false;
  competitionId: number;
  rowEvent: LazyLoadEvent;

  fieldTypes = [
    { id: 1, name: 'Text' },
    { id: 2, name: 'Number' },
    { id: 3, name: 'Date' },
    { id: 4, name: 'Boolean' },
    { id: 5, name: 'Upload' },
    { id: 6, name: 'Dropdown (Single)' },
    { id: 7, name: 'Dropdown (Multi)' }
  ];

  downloadingMap: { [key: string]: boolean } = {};

  displayDialog: boolean = false;
  selectedEntry: any = null;
  minDate: Date = new Date();
  rows: SelectItem[];
  filteredRecords: number;
  totalRecords: number;

  constructor(
        private service: CompetitionService, 
        private router: Router, 
        private route: ActivatedRoute,
        public storageService: StorageService,
        private auth: AuthenticationService,
        private cdr: ChangeDetectorRef) 
      {
        this.id = this.route.snapshot.paramMap.get('Id');
        this.rows = [];
        this.rows.push({ label: '10', value: 10 });
        this.rows.push({ label: '25', value: 25 });
        this.rows.push({ label: '50', value: 50 });
      }

    ngOnInit() {
      this.get();
      this.minDate = new Date();
    }
  
    showDialog(entry: any) {
      this.selectedEntry = { ...entry }; 
      this.displayDialog = true; 
    }

    exportXls() {
      const exportData = this.entries.map(entry => {
        return {
          UserName: entry.UserName,
          Description: entry.Description,
          IdNumber: entry.IdNumber,
          ContactNumber: entry.ContactNumber,
          Consent: entry.Consent,
          Profile: entry.Profile,
          File: entry.FileId ? `${this.auth.apiUrl}/api/storage/download/${entry.FileId}` : 'No file uploaded'
        };
      });
    
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      const workbook: XLSX.WorkBook = { Sheets: { 'Entries': worksheet }, SheetNames: ['Entries'] };
    
      const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, 'entries');
    }
    
    saveAsExcelFile(buffer: any, fileName: string): void {
      const data: Blob = new Blob([buffer], { type: 'application/octet-stream' });
      FileSaver.saveAs(data, `${fileName}_export_${new Date().getTime()}.xlsx`);
    }

    get() {
      this.loading = true;
      this.service.get(this.id).then(
        result => {
          this.model = new EditCompetition(result);
          this.entries = result.Entries;
          this.CustomFields = result.CustomFields;
          this.competitionId = result.Id;
          this.loading = false;
          this.cdr.detectChanges();
        },
        error => {
          this.loading = false;
          this.error = [];
          this.error.push({ severity: 'error', summary: 'Error', detail: error });
          this.cdr.detectChanges();
        });
    }

    loadEntries(event: LazyLoadEvent): void {
      this.entriesLoading = true;
      event.rows = this.entriesRows;
      this.rowEvent = event;
      this.service.getPaginatedEntries(this.competitionId, event).then(
        dataPager => {
          this.entries = dataPager.Data;
          this.filteredRecords = dataPager.FilteredRecords;
          this.totalRecords = dataPager.TotalRecords;
          this.entriesLoading = false;
        },
        error => {
          this.entriesLoading = false;
          this.entries = [];
          this.totalRecords = 0;
          this.filteredRecords = 0;
        }
      );
    }

    reloadEntries(event): void {
      this.loadEntries(this.rowEvent);
    }
  
    goBack(): void {
      this.router.navigate(['/competitions']);
    }

    save() {
      this.loading = true;
      this.service.update(this.model).then(
        result => {
          this.loading = false;
          this.model = new EditCompetition(result);
          this.error = [];
          this.error.push({ severity: 'success', summary: 'Success', detail: "Event saved" });
        },
        error => {
          this.loading = false;
          this.error = [];
          this.error.push({ severity: 'error', summary: 'Error', detail: error });
        });
    }

    onEnabledChange(value: boolean) {
      this.model.Enabled = value; 
    }

    downloadCompetitionFiles(competitionId: number, competitionName: string) {
      this.downloading = true;
    
      this.service.downloadMultipleFiles(competitionId, this.entriesRows).subscribe(
        (response: Blob) => {
          this.downloading = false;
    
          if (response.size === 0) {
            console.error('Empty blob received!');
            return;
          }
    
          const url = window.URL.createObjectURL(response);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${competitionName}_Files.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
    
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        },
        (error) => {
          this.downloading = false;
          this.downloading = false;
          this.error = [{ severity: 'error', summary: 'Download Failed', detail: error.message || 'Unknown error' }];
        }
      );
    }
    

    downloadPdf(fileId: string, username: string) {
      this.downloading = true;
    
      // Set downloading state for this file
      this.downloadingMap[fileId] = true;

      this.storageService.downloadFile(fileId).subscribe(
        (response: Blob) => {
          this.downloading = false;
          if (response.size === 0) {
            console.error('Empty blob received!');
            return;
          }
    
          const blob = response;
          const url = window.URL.createObjectURL(blob);
    
          let fileType = 'Document';
          let fileExtension = '.pdf'; 

          if (blob.type === 'application/pdf') {
            fileType = 'Document';
          } else if (blob.type === 'application/msword' || blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            fileType = 'Document';
            fileExtension = '.docx';
          } else if (blob.type.startsWith('video/')) {
            fileType = 'Video';
            
            // Convert QuickTime files to .mp4
            if (blob.type === 'video/quicktime') {
              fileExtension = '.mp4';
            } else {
              fileExtension = '.' + blob.type.split('/')[1]; // Extract the extension
            }
          } else if (blob.type.startsWith('image/')) {
            fileType = 'Image';
            fileExtension = '.' + blob.type.split('/')[1]; // Extract the extension
          }
    
          const downloadFileName = `${username}${fileType}${fileExtension}`;
    
          const a = document.createElement('a');
          a.href = url;
          a.download = downloadFileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
    
          setTimeout(() => window.URL.revokeObjectURL(url), 100);

          this.downloadingMap[fileId] = false;
        },
        (error) => {
          this.downloading = false;
          this.error = [{ severity: 'error', summary: 'Download Failed', detail: error.message || 'Unknown error' }];
        }
      );
    }

    addCustomField() {
      this.model.CustomFields.push({
        FieldName: '',
        FieldType: 1,
        IsRequired: false,
        CompetitionCustomFieldOptions: []
      });
    }
  
    removeCustomField(index: number) {
      this.model.CustomFields.splice(index, 1);
    }

    addDropdownOption(field: any) {

      field.CompetitionCustomFieldOptions.push({ OptionText: '' });
  
    }

    removeDropdownOption(field: any, index: number) {
      field.CompetitionCustomFieldOptions.splice(index, 1);
    }
  
    onFieldTypeChange(field: any) {
      field.FieldTypeId = field.FieldType;
      if (field.FieldTypeId !== 4) {
        field.Options = [];
      }
    }

    copyToClipboard(email: string) {
      const textarea = document.createElement('textarea');
      textarea.value = email;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('Email copied to clipboard!');
    }

  handleImage(event): void {
		var result = this.storageService.handleSuccess(event);
		
		this.model.ImageId = result.PublicId;
		this.loading = false;
	}

	handleError(event): void {
		this.loading = false;
		this.error = [];
		this.storageService.handleError(event, this.error);
	}

  getSelectedOptionTexts(reply: any): string {
    if (!reply.Replies || reply.Replies.length === 0) {
      return 'N/A';
    }
  
    // Filter out any Replies without SelectedOptionText
    const optionTexts = reply.Replies
      .filter(r => r.SelectedOptionText)
      .map(r => r.SelectedOptionText);
  
    return optionTexts.length > 0 ? optionTexts.join(', ') : 'N/A';
  }
  

	removeImage(): void {
		this.model.ImageId = null;
		this.loading = false;
	}

    

  
}



// WEBPACK FOOTER //
// ./src/app/components/competitions/edit-competition/edit-competition.component.ts